/**
 * UsernamePromptModal + UsernamePromptGate (KAN-328)
 * ===================================================
 *
 * Legacy-user prompt for the username system. Shown when:
 *   - the user has a session AND a profile, AND
 *   - `user.username_set_explicitly === false`, AND
 *   - the user hasn't dismissed it via the Defer button this session.
 *
 * The user can either accept the auto-generated handle (set_username
 * marks `username_set_explicitly = true`) or change it to something
 * else. Defer keeps `username_set_explicitly = false` and stores a
 * sessionStorage flag so the modal won't pop again until next sign-in.
 *
 * Mounted globally inside <AppRoutes /> so it covers every authed page.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertCircle, AtSign, X } from 'lucide-react';
import {
  useAuth,
  type UsernameRejectReason,
} from '../../contexts/SupabaseAuthContext';

// KAN-25485 (Will 2026-05-21): allowlist of "anonymous routes" the gate
// MUST stay off of. Even if a leaked Supabase session sneaks through (the
// IDB clear in SupabaseAuthContext logout + clearWebAuthStorage is the
// primary fix; this is defense-in-depth), the modal hijacking the sign-up
// / sign-in / landing form pre-filled with a prior test user's username
// broke fresh-user E2E walkthroughs. The user lands on these routes
// intending to be anonymous — never show the prompt regardless of what
// the auth state says.
//
// KAN-25551 follow-up (Will 2026-05-22): /upgrade + /m/upgrade are added
// because they're the *conversion* surface. A signed-in FREE user with
// username_set_explicitly=false who hits /upgrade?promo=FRIENDS-LAUNCH-2026
// must land directly on the pricing card + promo banner — NOT on a
// username modal that interrupts the funnel. NO FRICTION at the
// conversion moment. The gate still fires on every other authed route, so
// the username-completion ask still happens — just not at the worst
// possible moment.
const ANON_ROUTE_PREFIXES = [
  '/login',
  '/sign-up',
  '/sign-in',
  '/signup',
  '/signin',
  '/forgot-password',
  '/reset-password',
  '/upgrade',
  '/m/upgrade',
];

function isAnonRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const p = pathname.toLowerCase();
  return ANON_ROUTE_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + '/'));
}

const USERNAME_FORMAT_RE = /^[a-zA-Z0-9_]+$/;
const USERNAME_MIN = 3;
const USERNAME_MAX = 24;

const USERNAME_REJECT_COPY: Record<UsernameRejectReason, string> = {
  empty: 'Choose a username.',
  too_short: `Too short (min ${USERNAME_MIN} chars).`,
  too_long: `Too long (max ${USERNAME_MAX} chars).`,
  bad_chars: 'Letters, numbers, and underscores only.',
  reserved: 'That name is reserved — choose another.',
  taken: 'Already taken.',
  not_authenticated: 'Sign in first.',
};

function clientValidate(value: string): UsernameRejectReason | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return 'empty';
  if (trimmed.length < USERNAME_MIN) return 'too_short';
  if (trimmed.length > USERNAME_MAX) return 'too_long';
  if (!USERNAME_FORMAT_RE.test(trimmed)) return 'bad_chars';
  return null;
}

const SESSION_DEFER_KEY = 'aightbet:username-prompt-deferred';

interface UsernamePromptModalProps {
  /** Initial value (auto-derived handle from email local-part). */
  initialUsername: string;
  /** "legacy" = shown unprompted on first authed render; "edit" = user
   *  invoked it from Profile. Subtle copy difference. */
  mode: 'legacy' | 'edit';
  onClose: () => void;
  /** Only meaningful in legacy mode. Edit mode hides the Defer button —
   *  if the user opened it from Profile, dismissing without saving is
   *  always allowed via Cancel/X. */
  onDefer?: () => void;
}

export const UsernamePromptModal: React.FC<UsernamePromptModalProps> = ({
  initialUsername,
  mode,
  onClose,
  onDefer,
}) => {
  const { checkUsernameAvailability, setUsername: saveUsername } = useAuth();
  const [value, setValue] = useState(initialUsername);
  const [status, setStatus] = useState<
    | { state: 'idle' }
    | { state: 'checking' }
    | { state: 'available' }
    | { state: 'rejected'; reason: UsernameRejectReason }
  >({ state: 'idle' });
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Live availability check, debounced (400ms). Mirrors Login.tsx.
  useEffect(() => {
    const trimmed = value.trim();
    if (trimmed === '') {
      setStatus({ state: 'idle' });
      return;
    }
    const clientReject = clientValidate(trimmed);
    if (clientReject) {
      setStatus({ state: 'rejected', reason: clientReject });
      return;
    }
    setStatus({ state: 'checking' });
    let cancelled = false;
    const timer = setTimeout(async () => {
      const result = await checkUsernameAvailability(trimmed);
      if (cancelled) return;
      if (result.available) {
        setStatus({ state: 'available' });
      } else {
        setStatus({
          state: 'rejected',
          reason: (result.reason as UsernameRejectReason) || 'taken',
        });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, checkUsernameAvailability]);

  // Special case: in legacy mode, the initial auto-handle is the user's
  // own existing username. The availability RPC will return `taken` (their
  // own row matches). Treat "taken by self" as available so the user can
  // hit "Looks good" without typing anything.
  const isInitialUntouched = useMemo(
    () => value.trim() === initialUsername.trim() && initialUsername.trim() !== '',
    [value, initialUsername],
  );

  const effectiveStatus = useMemo(() => {
    if (
      isInitialUntouched &&
      status.state === 'rejected' &&
      status.reason === 'taken'
    ) {
      return { state: 'available' as const };
    }
    return status;
  }, [status, isInitialUntouched]);

  const canSave = effectiveStatus.state === 'available' && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const result = await saveUsername(value.trim());
      if (result.ok) {
        // Clear any stored deferral so future sessions don't think the
        // user has been asked already (they answered).
        try {
          sessionStorage.removeItem(SESSION_DEFER_KEY);
        } catch {
          /* SSR / restricted storage — non-fatal */
        }
        onClose();
      } else {
        const reason = (result.reason as UsernameRejectReason) || 'taken';
        setStatus({ state: 'rejected', reason });
        setSubmitError(USERNAME_REJECT_COPY[reason]);
      }
    } catch (e) {
      setSubmitError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDefer = () => {
    try {
      sessionStorage.setItem(SESSION_DEFER_KEY, '1');
    } catch {
      /* non-fatal */
    }
    onDefer?.();
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="username-prompt-title"
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.96 }}
        transition={{ duration: 0.2 }}
        className="bg-surface-elevated border border-white/10 rounded-xl shadow-card max-w-md w-full p-6 relative"
      >
        {mode === 'edit' && (
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 rounded-md text-white/40 hover:text-white/80 hover:bg-white/[0.06] inline-flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}

        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
            <AtSign className="w-5 h-5 text-brand" aria-hidden="true" />
          </div>
          <div>
            <h3
              id="username-prompt-title"
              className="text-lg font-semibold text-white"
            >
              {mode === 'legacy' ? 'Choose a username' : 'Change your username'}
            </h3>
            <p className="text-sm text-white/60 mt-1">
              {mode === 'legacy'
                ? 'This is the only thing other users see on leaderboards. Your name and email stay private.'
                : 'Your username is what shows on leaderboards and shared predictions.'}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="username-prompt-input"
            className="text-xs font-medium text-white/60 block"
          >
            Username
          </label>
          <div className="relative">
            <input
              id="username-prompt-input"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full bg-surface-base border border-white/10 rounded-md px-3 py-2.5 pr-10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-brand"
              placeholder="e.g. sharpfade24"
              minLength={USERNAME_MIN}
              maxLength={USERNAME_MAX}
              aria-describedby="username-prompt-hint"
              aria-invalid={effectiveStatus.state === 'rejected' ? true : undefined}
              autoFocus
            />
            {effectiveStatus.state === 'checking' && (
              <span
                aria-hidden="true"
                className="absolute right-3 top-1/2 -translate-y-1/2 inline-block w-3 h-3 rounded-full border-2 border-zinc-500 border-t-transparent animate-spin"
              />
            )}
            {effectiveStatus.state === 'available' && (
              <Check
                aria-hidden="true"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-signal-positive"
              />
            )}
            {effectiveStatus.state === 'rejected' && (
              <AlertCircle
                aria-hidden="true"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-signal-negative"
              />
            )}
          </div>
          <p
            id="username-prompt-hint"
            className={`text-[11px] leading-snug ${
              effectiveStatus.state === 'rejected'
                ? 'text-signal-negative'
                : effectiveStatus.state === 'available'
                  ? 'text-signal-positive'
                  : 'text-white/50'
            }`}
            role={effectiveStatus.state === 'rejected' ? 'alert' : undefined}
          >
            {effectiveStatus.state === 'rejected'
              ? USERNAME_REJECT_COPY[effectiveStatus.reason]
              : effectiveStatus.state === 'available'
                ? isInitialUntouched
                  ? 'Looks good — keep it, or choose a new one.'
                  : 'Available.'
                : `${USERNAME_MIN}–${USERNAME_MAX} chars · letters, numbers, underscores.`}
          </p>
          {submitError && (
            <p className="text-xs text-signal-negative" role="alert">
              {submitError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          {mode === 'legacy' && (
            <button
              type="button"
              onClick={handleDefer}
              disabled={saving}
              className="btn-secondary btn-sm"
            >
              Not now
            </button>
          )}
          {mode === 'edit' && (
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="btn-secondary btn-sm"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="btn-primary btn-sm"
          >
            {saving ? 'Saving…' : mode === 'legacy' ? 'Looks good' : 'Save'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/**
 * UsernamePromptGate — the global mount point. Drop into <AppRoutes /> once.
 * Reads `user.username_set_explicitly` and shows the modal in `legacy` mode
 * if false (and not deferred this session).
 */
export const UsernamePromptGate: React.FC = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const onAnonRoute = isAnonRoute(pathname);
  const [deferred, setDeferred] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SESSION_DEFER_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Re-read deferred flag whenever user identity changes — handles the
  // case where the user signs out (different session) without a full
  // page reload.
  useEffect(() => {
    if (!user) {
      try {
        sessionStorage.removeItem(SESSION_DEFER_KEY);
      } catch {
        /* non-fatal */
      }
      setDeferred(false);
    }
  }, [user?.id]);

  // Don't show until we have a real profile. The fallback user has
  // username_set_explicitly=false by default; firing the modal there
  // would race with the real profile fetch and might prompt for a
  // handle the user already set on signup. Wait for username to be
  // present (real profile landed) OR explicitly false (real profile
  // landed, legacy row).
  const shouldShow =
    // KAN-25485 — never render on anonymous routes (login / sign-up /
    // sign-in / password recovery). Even if a session sneaks through from
    // a leaked IDB token, the user is on a surface where the modal would
    // hijack a form they're trying to fill — defense-in-depth on top of
    // the IDB clear in SupabaseAuthContext.logout + clearWebAuthStorage.
    !onAnonRoute &&
    !!user &&
    user.username_set_explicitly === false &&
    !deferred &&
    // Wait for real profile fetch (auto-derived username will be present
    // post-trigger; legacy rows have it after the migration backfill).
    (user.username || '').length > 0;

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      <UsernamePromptModal
        key="legacy-username-prompt"
        mode="legacy"
        initialUsername={user!.username}
        onClose={() => setDeferred(true)}
        onDefer={() => setDeferred(true)}
      />
    </AnimatePresence>
  );
};

export default UsernamePromptGate;
